import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCheckoutPageProps from "@hooks/useCheckoutPageProps";
import useEnrolPageProps from "@hooks/useEnrolPageProps";
import useResetAtomicState from "@hooks/useResetAtomicState";

import useAppStateSync from "@/hooks/useAppStateSync";
import { CheckoutContext } from "@/providers/context/CheckoutContext";
import { dispatchGAEvents } from "@/utils/events/dispatchGAEvents";
import { getTrackingId } from "@/utils/utils";
import { useAtom, useAtomValue } from "jotai";

import { Flex } from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";

import {
    addAddonAtom,
    addSubjectChangesAtom,
    checkoutAtom,
    checkoutTotalAmountAtom,
    couponAtom,
    createSubscriptionAtom,
    heapTrackingAtom,
    isNewsletterSubEnabledAtom,
    pageQueryParamsAtom,
    parentIdAtom,
    userCheckoutStateTokenAtom
} from "@atoms/checkoutAtom";

import { dispatchHSEvents } from "@utils/events/dispatchHSEevents";

import ChargePurchaseSuccess from "./components/ChargePurchaseSuccess";
import { CheckoutDetailsContainer } from "./containers/CheckoutDetailsContainer";
import { PaymentCaptureContainer } from "./containers/PaymentCaptureContainer";
import { PriceIdApiInterface } from "./containers/PaymentCaptureContainer/PaymentCaptureContainer";

const CheckoutContainer = () => {
    const { hubspotEventCheckout, addonTextbookLink, parentUser } =
        useCheckoutPageProps();
    const { hubspotEventEnrol } = useEnrolPageProps();

    const [priceIds, setPriceIds] = useState<PriceIdApiInterface[]>([]);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);

    const [transactionProducts, setTransactionProducts] = useState<
        {
            item_id: string;
            item_name: string;
            item_category: string;
            item_grade: string | null;
            item_state: string;
            price: number;
            quantity: number;
        }[]
    >([]);
    const [parentId] = useAtom(parentIdAtom);

    const [checkoutTotalAmount] = useAtom(checkoutTotalAmountAtom);

    const [coupon] = useAtom(couponAtom);
    const createSubscriptionState = useAtomValue(createSubscriptionAtom);
    const addAddonState = useAtomValue(addAddonAtom);
    const addSubjectChangesState = useAtomValue(addSubjectChangesAtom);
    const [checkout, setCheckout] = useAtom(checkoutAtom);
    const [userCheckoutStateToken] = useAtom(userCheckoutStateTokenAtom);
    const isNewsletterSubEnabled = useAtomValue(isNewsletterSubEnabledAtom);

    const [isHundredPercentOff, setIsHundredPercentOff] = useState(false);

    const {
        state: { checkout: checkoutAppState },
        updateFlows: { resetCheckoutAppState }
    } = useAppStateSync();

    const onApplyHundredPercentOff = (state: boolean) => {
        setIsHundredPercentOff(state);
    };

    const { resetCheckoutAtomicState } = useResetAtomicState();

    const isParentUserLoggedIn = !!parentUser;

    const successPageDataRef = useRef<{
        entitlementsNames: string[];
        isBulkAddon: boolean;
    }>({
        entitlementsNames: [],
        isBulkAddon: false
    });

    const navigate = useNavigate();

    const onDispatchGAEvents = useCallback(() => {
        dispatchGAEvents({
            event: "purchase",
            ecommerce: {
                transaction_id: checkout.productFamily!.name,
                value: checkoutTotalAmount,
                tax: 0,
                shipping: 0,
                currency: checkout.plan!.currency,
                coupon,
                items: transactionProducts
            },
            parentId
        });
    }, [checkout, checkoutTotalAmount]);

    const checkoutSubjectsFee = checkoutAppState?.checkoutSubjectsFee ?? null;

    const checkoutContext = useContext(CheckoutContext);

    const { userSelectedCountry, userSelectedState, userSelectedLang, productFamilies } =
        checkoutContext;

    const [heapTrackingData, setHeapTracking] = useAtom(heapTrackingAtom);

    useEffect(() => {
        if (heapTrackingData && window.heap?.track) {
            window.heap.track(
                heapTrackingData.eventName,
                heapTrackingData.data
            );
        }
    }, [heapTrackingData]);

    useEffect(() => {
        dispatchHSEvents(
            "trackCustomBehavioralEvent",
            undefined,
            {
                email: checkout.user.email,
                checkout_token: userCheckoutStateToken,
                newsletter_subscription: isNewsletterSubEnabled ? "yes" : "no",
                checkout_indicative_plan: pageQueryParams?.plan,
                selected_country: userSelectedCountry,
                hs_language: userSelectedLang,
            },
            "checkout",
            hubspotEventCheckout,
            hubspotEventEnrol
        );

        const handleRedirectionAfterSubscription = () => {
            dispatchHSEvents(
                "trackPageView",
                CheckoutPageRoutesEnum.PAYMENT_SUCCESS_PAGE,
                {
                    checkout_status: "PAID",
                    email: checkout.user.email,
                    checkout_token: userCheckoutStateToken,
                    selected_country: userSelectedCountry,
                    hs_language: userSelectedLang,
                },
                "checkout",
                hubspotEventCheckout,
                hubspotEventEnrol
            );

            dispatchHSEvents(
                "trackCustomBehavioralEvent",
                CheckoutPageRoutesEnum.PAYMENT_SUCCESS_PAGE,
                {
                    checkout_status: "PAID",
                    checkout_token: userCheckoutStateToken,
                    email: checkout.user.email,
                    selected_country: userSelectedCountry,
                    hs_language: userSelectedLang,
                    newsletter_subscription: isNewsletterSubEnabled
                        ? "yes"
                        : "no"
                },
                "checkout",
                hubspotEventCheckout,
                hubspotEventEnrol
            );

            // If the checkout app was accessed from a place where it expects a redirection back, upon checkout completion
            if (isParentUserLoggedIn) {
                resetCheckoutAppState(undefined, {
                    hasPendingSubCreated:
                        !!checkoutAppState?.checkoutRedirectsTo,
                    checkoutFlow: null
                });
                resetCheckoutAtomicState();

                // redirect only after cleaning up previous atoms
                window.location.href =
                    checkoutAppState?.checkoutRedirectsTo ??
                    route("parents.dashboard");
                history.replaceState({}, document.title, route("checkout"));
            } else {
                if (userSelectedCountry === "VN") {
                    window.location.href = route(`external.ecom.payment-success-${userSelectedCountry.toLowerCase()}-${userSelectedLang.toLowerCase()}`, {
                        firstName: checkout.user.firstName,
                        email: checkout.user.email
                    });
                } else {
                    window.location.href = route("external.ecom.payment-success", {
                        firstName: checkout.user.firstName,
                        email: checkout.user.email
                    });
                }

                history.replaceState({}, document.title, route("checkout"));
            }
        };

        const handleRedirectionAfterAddonAdd = () => {
            resetCheckoutAppState();
            resetCheckoutAtomicState();
        };

        const handleRedirectionAfterSubjectChanges = () => {
            resetCheckoutAppState();
            resetCheckoutAtomicState();

            // redirect only after cleaning up previous atoms
            window.location.href =
                checkoutAppState?.checkoutRedirectsTo ??
                route("parents.dashboard");
            history.replaceState({}, document.title, route("checkout"));
        };

        if (createSubscriptionState.isSuccess) {
            handleRedirectionAfterSubscription();
        }

        if (addAddonState.isSuccess) {
            handleRedirectionAfterAddonAdd();
        }

        if (addSubjectChangesState.isSuccess) {
            handleRedirectionAfterSubjectChanges();
        }
    }, [
        createSubscriptionState.isSuccess,
        addAddonState.isSuccess,
        addSubjectChangesState.isSuccess
    ]);

    useEffect(() => {
        if (checkoutAppState?.checkoutPurchasingAddon && !checkout.addon) {
            if (checkoutAppState?.checkoutPurchasingAddon) {
                // TODO: Improve hardcorded check for contries don't need state selections
                navigate(
                    userSelectedCountry === "VN"
                        ? CheckoutPageRoutesEnum.ADDON_SELECT_PAGE
                        : CheckoutPageRoutesEnum.STATE_SELECT_PAGE
                );
            }
            if (checkoutAppState?.checkoutSubjectsFee) {
                navigate(CheckoutPageRoutesEnum.PAYMENT_PAGE);
            }
        }
    }, [checkout]);

    useEffect(() => {
        successPageDataRef.current.entitlementsNames =
            checkout.addon?.entitlementsNames ?? [];
        successPageDataRef.current.isBulkAddon =
            checkout.addon?.isBulkAddon ?? false;
    }, [checkout]);

    useEffect(() => {
        const preparePriceIds = () => {
            const priceIds: PriceIdApiInterface[] = [];
            if (checkout.plan) {
                priceIds.push({
                    item_price_id: checkout.plan.priceId,
                    item_type: "plan"
                });

                /**
                 * Check to see if the selected plan has addons which are included by default.
                 */
                const defaultIncludedAddon = checkout.plan.addons
                    ? checkout.plan.addons.find(a => a.isIncluded) ?? null
                    : null;
                if (defaultIncludedAddon) {
                    priceIds.push({
                        item_price_id: defaultIncludedAddon.priceId,
                        item_type: "addon"
                    });
                }
            }
            if (checkout.addon) {
                priceIds.push({
                    item_price_id: checkout.addon.priceId,
                    item_type: "addon"
                });
            }
            setPriceIds(priceIds);
        };

        const prepareDataLayerItem = () => {
            const transactionProducts = [];
            if (checkout.plan) {
                transactionProducts.push({
                    item_id: checkout.plan.id,
                    item_name: checkout.plan.name,
                    item_category: "plan",
                    item_grade: checkout.grade ? checkout.grade.name : null,
                    item_state: userSelectedState,
                    price: checkout.plan.price,
                    quantity: 1
                });

                if (checkout.addon) {
                    transactionProducts.push({
                        item_id: checkout.addon.id,
                        item_name: checkout.addon.name,
                        item_category: "addon",
                        item_grade: checkout.grade ? checkout.grade.name : null,
                        item_state: userSelectedState,
                        price: checkout.addon.price,
                        quantity: 1
                    });
                }

                // Setting Heap data via useState.
                const heapData = {
                    transaction_id: checkout.productFamily!.name,
                    value: checkoutTotalAmount,
                    tax: 0,
                    shipping: 0,
                    currency: checkout.plan!.currency,
                    coupon,
                    items: transactionProducts
                };
                setHeapTracking({ eventName: "purchase", data: heapData });
                setTransactionProducts(transactionProducts);
            }
        };

        preparePriceIds();
        prepareDataLayerItem();
    }, [checkout, checkoutTotalAmount]);

    /**
     * Constructing the checkout based on the subject payment flow.
     */
    useEffect(() => {
        if (productFamilies !== null) {
            if (checkoutSubjectsFee) {
                const productFamily =
                    productFamilies.find(
                        pF => pF.id === checkoutSubjectsFee.productFamilyId
                    ) ?? null;

                const grade = productFamily
                    ? productFamily.grades.find(
                    g => g.name === checkoutSubjectsFee.grade
                ) ?? null
                    : null;

                const plan = productFamily
                    ? productFamily.plans.find(
                    p => p.id === checkoutSubjectsFee.planId
                ) ?? null
                    : null;

                setCheckout(prev => ({
                    ...prev,
                    productFamily,
                    grade,
                    plan
                }));
            }
        }
    }, [productFamilies]);

    if (addAddonState.isSuccess) {
        const serviceName = successPageDataRef.current.isBulkAddon
            ? `${successPageDataRef.current.entitlementsNames[0].replace(
                /Service/g,
                ""
            )} and ${successPageDataRef.current.entitlementsNames[1]}`
            : successPageDataRef.current.entitlementsNames[0];

        return (
            <ChargePurchaseSuccess
                addonTextbookLink={addonTextbookLink ?? ""}
                redirectsTo={checkoutAppState?.checkoutRedirectsTo ?? ""}
                serviceName={serviceName}
                isBulkAddon={successPageDataRef.current.isBulkAddon}
            />
        );
    }

    return (
        <Flex direction={["column", null, null, "row"]} flex={1}>
            <Flex
                flex={[null, null, "0.5"]}
                className={ClassesEnum.PaymentDetailsContainer}
                data-testid="checkout-details-container"
                data-tracking-id={getTrackingId(
                    [ClassesEnum.PaymentDetailsContainer],
                    "checkoutDetailsContainer"
                )}
            >
                <CheckoutDetailsContainer
                    onApplyHundredPercentOff={onApplyHundredPercentOff}
                    priceIds={priceIds}
                />
            </Flex>
            <Flex
                flex={[null, null, "0.5"]}
                pb={4}
                className={ClassesEnum.CardDetailsContainer}
                data-testid="payment-capture-container"
                data-tracking-id={getTrackingId(
                    [ClassesEnum.CardDetailsContainer],
                    "paymentCaptureContainer"
                )}
            >
                <PaymentCaptureContainer
                    isHundredPercentOff={isHundredPercentOff}
                    priceIds={priceIds}
                    onTrackWithHeap={eventName =>
                        setHeapTracking({ eventName, data: heapTrackingData })
                    }
                    onDispatchGAEvents={onDispatchGAEvents}
                />
            </Flex>
        </Flex>
    );
};

export default CheckoutContainer;
